import { sanitizeUrl } from '@braintree/sanitize-url';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { CognitoUser } from 'amazon-cognito-identity-js';
import LogoText from 'atoms/LogoText';
import NextLink from 'atoms/NextLink';
import cn from 'classnames';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import { observer } from 'mobx-react-lite';
import ProgressSteps from 'molecules/ProgressSteps';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import SignInForm from 'organisms/Registration/SignInForm';
import SignUpForm from 'organisms/Registration/SignUpForm';
import React, { useEffect, useRef, useState } from 'react';
import { useStore } from 'stores/store';
import { updateCampaignCookie } from 'utils/analytics';
import {
  getAuthDetails,
  getVerifiedUserDetails,
  IAuthData,
  IAuthDetails,
  navigateToHome,
  SIGNUP_COOKIE
} from 'utils/authUtils';
import { getCookie } from 'utils/cookies';
import { isBrowser } from 'utils/domUtils';
import { IOfferingInfo, IResourceData, ISponsorData } from 'utils/globalTypes';
import { useUserAuth } from 'utils/hooks/useUserAuth';
import { navigateToUrl } from 'utils/navigateUtils';
import { FORGOT_PASSWORD, getSingleValue, getValidUrl, SIGN_IN, SIGN_UP } from 'utils/urlUtils';

import s from './ConnectWrapper.module.scss';

const ConfirmSignInForm = dynamic(() => import('organisms/Registration/ConfirmSignInForm'));
const ConfirmSignUpForm = dynamic(() => import('organisms/Registration/ConfirmSignUpForm'));
const ForgotPasswordForm = dynamic(() => import('organisms/Registration/ForgotPasswordForm'));
const RequireNewPasswordForm = dynamic(
  () => import('organisms/Registration/RequireNewPasswordForm')
);
const VerifyEmailForm = dynamic(() => import('organisms/Registration/VerifyEmailForm'));
const WelcomeForm = dynamic(() => import('organisms/Registration/WelcomeForm'));
const DoneForm = dynamic(() => import('organisms/Registration/DoneForm'));
const ProfessionForm = dynamic(() => import('organisms/Registration/ProfessionForm'));
const PersonalDetailsForm = dynamic(() => import('organisms/Registration/PersonalDetailsForm'));
const AddressForm = dynamic(() => import('organisms/Registration/AddressForm'));
const NotAllowedForm = dynamic(() => import('organisms/Registration/NotAllowedForm'));
const LiquidityForm = dynamic(() => import('organisms/Registration/LiquidityForm'));
const InvestmentValueForm = dynamic(() => import('organisms/Registration/InvestmentValueForm'));
const InvestmentExperienceForm = dynamic(
  () => import('organisms/Registration/InvestmentExperienceForm')
);
const InvestmentObjectiveForm = dynamic(
  () => import('organisms/Registration/InvestmentObjectiveForm')
);
const ConnectWrapperGallery = dynamic(() => import('./ConnectWrapperGallery'));

const UnAuthenticatedStates = [
  SIGN_IN,
  SIGN_UP,
  'confirmSignIn',
  'confirmSignUp',
  FORGOT_PASSWORD,
  'requireNewPassword',
  'verifyEmail'
];

const ConnectForms = {
  welcome: {},
  notAccredited: {
    prev: 'welcome'
  },
  personal: {
    prev: 'welcome',
    next: 'address'
  },
  address: {
    prev: 'personal',
    next: 'investmentExperience'
  },
  investmentExperience: {
    prev: 'address',
    next: 'investmentObjective'
  },
  investmentObjective: {
    prev: 'investmentExperience',
    next: 'investmentValue'
  },
  investmentValue: {
    prev: 'investmentObjective'
  },
  profession: {
    prev: 'investmentValue',
    next: 'liquidity'
  },
  liquidity: {
    prev: 'profession'
  },
  notSophisticated: {
    prev: 'liquidity'
  },
  signIn: {
    showNewAccount: true
  },
  signUp: {
    showHaveAnAccount: true
  },
  confirmSignIn: {
    showBackToSignIn: true
  },
  confirmSignUp: {
    showBackToSignIn: true
  },
  forgotPassword: {
    showBackToSignIn: true
  },
  requireNewPassword: {
    showBackToSignIn: true
  },
  verifyEmail: {
    showBackToSignIn: true
  }
} as {
  [key: string]: {
    prev?: string;
    next?: string;
    showHaveAnAccount?: boolean;
    showBackToSignIn?: boolean;
    showNewAccount?: boolean;
  };
};

const ConnectWrapper = ({
  images,
  partners,
  sponsors,
  defaultState
}: {
  images?: IResourceData[];
  partners?: IResourceData[];
  sponsors?: ISponsorData[];
  defaultState?: string;
}) => {
  const store = useStore();
  const authDetails = useUserAuth();
  const router = useRouter();

  const [connectState, setConnectState] = useState<string>();
  const [requestPending, setRequestPending] = useState(false);
  const [token, setToken] = useState<string>();
  const [code, setCode] = useState<string>();
  const [email, setEmail] = useState<string>();
  const [sponsorId, setSponsorId] = useState<string>();
  const [password, setPassword] = useState<string>();
  const [user, setUser] = useState<CognitoUser>();
  const [authData, setAuthData] = useState<IAuthData>();
  const [offerings, setOfferings] = useState<Array<IOfferingInfo>>();
  const [url, setUrl] = useState<string>();
  const [contentHeight, setContentHeight] = useState<number>();
  const content = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (connectState !== SIGN_UP) {
      return;
    }
    setContentHeight(content?.current?.offsetHeight);
  }, [connectState]);

  const navigateToStart = async (authenticated?: boolean) => {
    if (authenticated || authDetails.isAuthenticated) {
      return navigateToUrl(url ? url : '/marketplace');
    }
  };

  const onNextAuth = async (
    authState: string,
    data: IAuthData,
    redirect = true,
    queriesToOmit?: string[]
  ) => {
    console.log('onNextAuth', authState);

    let currentAuthDetails = authDetails;

    setAuthData(data);
    if (data instanceof CognitoUser) {
      setUser(data);
    } else if (data) {
      if ('email' in data) {
        setEmail(data.email as string);
      }
      if ('code' in data) {
        setCode(data.code as string);
      }
      if ('password' in data) {
        setPassword(data.password as string);
      }
    }

    if (authState == 'signedIn' || authState == 'verifyContact') {
      if (data instanceof CognitoUser) {
        currentAuthDetails = getAuthDetails(data.getSignInUserSession());

        const userDetails = getVerifiedUserDetails(data?.getSignInUserSession());
        if (!userDetails) {
          try {
            await store.ProfileStore.fetchProfile();
          } catch (err) {
            changeState(SIGN_IN, queriesToOmit);
            return;
          }
        }
      }

      const isApproved =
        currentAuthDetails.isApproved ||
        (data instanceof CognitoUser && getAuthDetails(data?.getSignInUserSession())?.isApproved);

      if (isApproved) {
        if (redirect) {
          await navigateToStart(true);
        }
        setRequestPending(false);
      } else {
        if (currentAuthDetails?.isAuthenticated && (sponsorId || currentAuthDetails.sponsorId)) {
          fetchOfferings();
        }

        changeState(getValidState(currentAuthDetails, 'welcome'), queriesToOmit);
      }
    } else {
      changeState(getValidState(currentAuthDetails, authState), queriesToOmit);
    }
  };

  const fetchOfferings = () => {
    store.OfferingStore.listInvestorOfferings(2).then((response) => {
      setOfferings(response?.data);
    });
  };

  const onPrev = () => {
    if (connectState) {
      const connectForm = ConnectForms[connectState];
      if (connectForm?.prev) {
        changeState(connectForm?.prev);
      }
    }
  };

  const onNext = () => {
    if (connectState) {
      const connectForm = ConnectForms[connectState];
      if (connectForm?.next) {
        changeState(connectForm?.next);
      }
    }
  };

  const onNextWelcome = async () => {
    if (
      store.ProfileStore.userData?.financialDetails?.accredited ||
      authDetails?.investorAccredited
    ) {
      changeState('personal');
    } else {
      changeState('notAccredited');
    }
  };

  const onNextLiquidity = async () => {
    if (store.ProfileStore.userData?.verified) {
      changeState('done');
    } else {
      changeState('notSophisticated');
    }
  };

  const isUserAccredited = () => {
    return (
      store.ProfileStore.userData?.financialDetails?.accredited || authDetails?.investorAccredited
    );
  };

  const isCheckUserOfferings = () => {
    return (
      isEmpty(offerings) && authDetails?.isAuthenticated && (sponsorId || authDetails?.sponsorId)
    );
  };

  const onNextNotAllowed = async () => {
    navigateToHome();
  };

  const onNextInvestmentValue = async () => {
    if (!sponsorId || !store.ProfileStore.isVerified) {
      changeState('profession');
    } else {
      await changeState('done');
    }
  };

  const changeState = (state: string, queriesToOmit?: string[]) => {
    if (!isBrowser()) {
      return;
    }

    const path = `/connect/${state}`;

    if (router && !router?.asPath.startsWith(path)) {
      router?.replace(
        {
          pathname: sanitizeUrl(path),
          query: omit(router?.query, [...(queriesToOmit ?? []), 'all'])
        },
        undefined,
        { shallow: true, scroll: true }
      );
    }

    if (
      (state == 'welcome' || (state == 'personal' && isUserAccredited())) &&
      isCheckUserOfferings()
    ) {
      fetchOfferings();
    }

    setConnectState(state);
    setRequestPending(false);
  };

  const onNextDone = async () => {
    await navigateToStart(true);
  };

  const getValidState = (authDetails: IAuthDetails, state?: string) => {
    let validState;
    if (state && (authDetails.isAuthenticated || UnAuthenticatedStates.includes(state))) {
      validState = state;
    } else {
      validState = !authDetails.isAuthenticated
        ? SIGN_IN
        : authDetails.isApproved
        ? 'done'
        : 'welcome';
    }

    if (validState == 'welcome' && authDetails.investorAccredited) {
      validState = 'personal';
    }

    return validState;
  };

  useEffect(() => {
    if (!authDetails.isAuthenticated) return;
    (async () => {
      await store.ProfileStore.fetchProfile();
    })();
  }, []);

  useEffect(() => {
    if (!router?.isReady) return;
    updateCampaignCookie(router);

    try {
      const currentState = getValidState(authDetails, defaultState);

      let regParams = router?.query;
      if (!regParams?.sponsor) {
        const cookieParams = getCookie(SIGNUP_COOKIE);
        if (!!cookieParams?.sponsor) {
          regParams = cookieParams;
        }
      }

      const email = getSingleValue(regParams?.email);
      if (email) {
        setEmail(email);
      } else if (authDetails.email) {
        setEmail(authDetails.email);
      }

      const url = getSingleValue(regParams?.url);
      if (url) {
        setUrl(url);
      }

      const sponsor = getSingleValue(regParams?.sponsor);
      if (sponsor) {
        setSponsorId(sponsor);
      }

      const code = getSingleValue(regParams?.code);
      if (code) {
        setCode(code);
      }

      const token = getSingleValue(regParams?.token);
      if (token) {
        setToken(token);
      }

      changeState(currentState);
    } catch (e) {
      console.error('Failed to render connect screen', e);
      changeState(SIGN_IN);
    }
  }, [router?.isReady]);

  const defaultImage =
    images && images.filter((image) => image?.name?.toLowerCase().endsWith('default'))[0];
  const stateImage =
    connectState &&
    images &&
    images.filter(
      (image) =>
        image?.name?.toLowerCase().endsWith('default') &&
        image?.description?.toLowerCase().includes(connectState.toLowerCase() || SIGN_IN)
    )[0];
  const sponsorStateImage =
    sponsorId &&
    connectState &&
    images &&
    images.filter(
      (image) =>
        image?.name?.endsWith(sponsorId) &&
        image?.description
          ?.toLowerCase()
          ?.split(/\r?\n/)
          ?.includes(connectState.toLowerCase() || SIGN_IN)
    )[0];
  const image = sponsorStateImage || stateImage || defaultImage;

  const galleryImages =
    (images &&
      images.filter(
        (image) =>
          image?.name?.toLowerCase().endsWith('default') ||
          image?.description?.toLowerCase().includes('default')
      )) ||
    [];

  const withSponsorImage = !!sponsorStateImage && image?.url;
  const withCarouselImages = !sponsorStateImage && images;

  const progressSteps = [
    'personal',
    'address',
    'investmentExperience',
    'investmentObjective',
    'investmentValue',
    'profession',
    'liquidity',
    'done/notSophisticated'
  ];

  const showSteps = progressSteps.some((step) => {
    const current = step.split('/');
    return connectState ? current.includes(connectState) : false;
  });

  const getForm = () => {
    switch (connectState) {
      case 'verifyEmail':
        return (
          <VerifyEmailForm
            token={token}
            email={email}
            requestPending={requestPending}
            setRequestPending={setRequestPending}
            onStateChange={onNextAuth}
          />
        );
      case SIGN_UP:
        return (
          <SignUpForm
            email={email}
            sponsorId={sponsorId}
            sponsors={sponsors}
            partners={partners}
            requestPending={requestPending}
            setRequestPending={setRequestPending}
            onStateChange={onNextAuth}
            onRedirect={changeState}
          />
        );
      case 'confirmSignIn':
        return (
          <ConfirmSignInForm
            requestPending={requestPending}
            setRequestPending={setRequestPending}
            onStateChange={onNextAuth}
            user={user}
            authData={authData}
          />
        );
      case 'confirmSignUp':
        return (
          <ConfirmSignUpForm
            requestPending={requestPending}
            setRequestPending={setRequestPending}
            onStateChange={onNextAuth}
            authData={authData}
            email={email}
            password={password}
          />
        );
      case FORGOT_PASSWORD:
        return (
          <ForgotPasswordForm
            requestPending={requestPending}
            setRequestPending={setRequestPending}
            onStateChange={onNextAuth}
            email={email}
            code={code}
          />
        );
      case 'requireNewPassword':
        return (
          <RequireNewPasswordForm
            requestPending={requestPending}
            setRequestPending={setRequestPending}
            onStateChange={onNextAuth}
            user={user}
          />
        );
      case 'welcome':
        return (
          <WelcomeForm
            offerings={offerings}
            requestPending={requestPending}
            setRequestPending={setRequestPending}
            onNext={onNextWelcome}
          />
        );
      case 'notAccredited':
        return (
          <NotAllowedForm
            requestPending={requestPending}
            setRequestPending={setRequestPending}
            notAccredited={true}
            onNext={onNextNotAllowed}
            onPrev={onPrev}
          />
        );
      case 'notSophisticated':
        return (
          <NotAllowedForm
            requestPending={requestPending}
            setRequestPending={setRequestPending}
            notAccredited={false}
            onNext={onNextNotAllowed}
            onPrev={onPrev}
          />
        );
      case 'personal':
        return (
          <PersonalDetailsForm
            requestPending={requestPending}
            setRequestPending={setRequestPending}
            sponsors={sponsors}
            onNext={onNext}
            onPrev={onPrev}
          />
        );
      case 'address':
        return (
          <AddressForm
            requestPending={requestPending}
            setRequestPending={setRequestPending}
            onNext={onNext}
            onPrev={onPrev}
          />
        );
      case 'investmentExperience':
        return (
          <InvestmentExperienceForm
            requestPending={requestPending}
            setRequestPending={setRequestPending}
            onNext={onNext}
            onPrev={onPrev}
          />
        );
      case 'investmentObjective':
        return (
          <InvestmentObjectiveForm
            requestPending={requestPending}
            setRequestPending={setRequestPending}
            onNext={onNext}
            onPrev={onPrev}
          />
        );
      case 'investmentValue':
        return (
          <InvestmentValueForm
            requestPending={requestPending}
            setRequestPending={setRequestPending}
            onNext={onNextInvestmentValue}
            onPrev={onPrev}
          />
        );
      case 'profession':
        return (
          <ProfessionForm
            requestPending={requestPending}
            setRequestPending={setRequestPending}
            onNext={onNext}
            onPrev={onPrev}
          />
        );
      case 'liquidity':
        return (
          <LiquidityForm
            requestPending={requestPending}
            setRequestPending={setRequestPending}
            onNext={onNextLiquidity}
            onPrev={onPrev}
          />
        );
      case 'done':
        return (
          <DoneForm
            sponsorId={sponsorId}
            offerings={offerings}
            requestPending={requestPending}
            setRequestPending={setRequestPending}
            onStateChange={onNextAuth}
            onNext={onNextDone}
          />
        );
      case SIGN_IN:
        return (
          <SignInForm
            requestPending={requestPending}
            setRequestPending={setRequestPending}
            onStateChange={onNextAuth}
            email={email}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={cn(s.ConnectWrapper, { signUp: connectState === SIGN_UP })}>
      <div
        ref={content}
        className={cn(s.ConnectWrapper__content, { signUp: connectState === SIGN_UP })}
      >
        <div className={cn(s.ConnectWrapper__header)}>
          <NextLink href={'/marketplace'} key={'homepage'}>
            <a>
              <LogoText className={s.ConnectWrapper__logo} />
            </a>
          </NextLink>
          {connectState && ConnectForms[connectState]?.showBackToSignIn && (
            <Typography variant="caption" color="text.secondary" ml={2}>
              {'Back to '}
              <Link component="button" color="inherit" onClick={() => changeState(SIGN_IN)}>
                Sign in
              </Link>
              {/* <a className={s.ConnectWrapper__action__link} onClick={() => changeState(SIGN_IN)}>
                Sign in
              </a> */}
            </Typography>
          )}
        </div>
        <div className={s.ConnectWrapper__form}>
          {showSteps && <ProgressSteps activeStep={connectState} steps={progressSteps} />}
          {getForm()}
        </div>
      </div>
      {connectState && connectState !== SIGN_UP && image?.url && (
        <div
          style={{ backgroundImage: `url(${getValidUrl(image?.url)})` }}
          className={s.ConnectWrapper__image}
        />
      )}

      {connectState === SIGN_UP && withSponsorImage && (
        <div
          style={{ backgroundImage: `url(${getValidUrl(image?.url)})` }}
          className={s.ConnectWrapper__image}
        />
      )}

      {connectState === SIGN_UP && withCarouselImages && (
        <ConnectWrapperGallery minHeight={contentHeight} images={galleryImages} />
      )}
    </div>
  );
};

export default observer(ConnectWrapper);
